import type { AppDispatch } from 'redux/storeConfig/store';
import type { RootState } from 'redux/reducers/rootReducer';

import axios from 'axios';

import clientApi from 'api/clientApi';
import { CONTACT_MESSAGE, Endpoints } from 'utility/constants';
import {
  generateParams,
  handleError,
  MemberActionTypes,
  setSelectedRow,
} from 'redux/actions/member/index';
import { showToast } from 'utility/toast';
import { store } from 'redux/storeConfig/store';

function getMemberState(): RootState['member'] {
  return (store.getState() as RootState).member;
}

/**
 * Dealers running Reconditioning Phases work their internal repair orders on the
 * Reconditioning dashboard, so the repair-order lists leave them out by default.
 *
 * That exclusion has to travel to the API. Dropping the rows client-side after the server
 * had already counted and paged them is what left the Internal filter chip rendering an
 * empty table beneath a non-zero record count, and short-changed every page besides
 * (ISA-4635). An explicitly chosen RO Type value always wins over this default — including
 * "All" (ISA-4633), whose value is `undefined` on the wire, so presence of the `internal` key
 * (not its value) is what distinguishes "explicitly chose All" from "no RO Type filter applied".
 */
function applyReconditioningInternalDefault(
  params: Record<string, unknown>,
): Record<string, unknown> {
  // The API sends settings as strings, but reconditioningPhases is in BOOLEAN_SETTINGS, so the
  // reducer has already run it through toBool by the time it lands here — compare against the
  // boolean, not '1'. Undefined (settings not loaded yet) correctly means "do not exclude".
  const hidesInternalRepairOrders = getMemberState().settings?.reconditioningPhases === true;

  if (!hidesInternalRepairOrders || 'internal' in params) {
    return params;
  }

  return { ...params, internal: false };
}

function buildRosParams(requestParams?: Record<string, unknown>): Record<string, unknown> {
  return applyReconditioningInternalDefault(
    Object.assign({ ...generateParams(), pageLimit: 50 }, requestParams),
  );
}

// in customers, note, & quote
export const getRoById = (id: number | string) => async (dispatch: AppDispatch) => {
  dispatch({ type: MemberActionTypes.LOADING_START });

  try {
    const response = await clientApi.get(`${Endpoints.ros}/${id}`);
    dispatch({ type: 'SET_REPAIR_ORDERS', value: [response.data] });
    dispatch(
      setSelectedRow({
        row: response.data,
      }),
    );
    return true;
  } catch (error) {
    dispatch(
      setSelectedRow({
        row: null,
      }),
    );
    dispatch(handleError(error));
    return false;
  } finally {
    dispatch({ type: MemberActionTypes.LOADING_END });
  }
};

export const deleteRosWithinDateRange = (dateRange: Date[]) => async (dispatch: AppDispatch) => {
  dispatch({ type: 'DELETE_ROS_WITHIN_DATE_RANGE' });

  try {
    if (!dateRange[0] || !dateRange[1]) {
      throw new Error('Date range must have a start and end.');
    }
    const openDateStart = dateRange[0].toISOString().slice(0, 10);
    const openDateEnd = dateRange[1].toISOString().slice(0, 10);

    // Params serializer may not work for DELETE
    await clientApi.delete(
      `${Endpoints.ros}?openDateStart=${openDateStart}&openDateEnd=${openDateEnd}`,
    );

    showToast({ message: 'ROs Deleted Successfully' });
    return true;
  } catch (error) {
    dispatch(handleError(error));
    return false;
  }
};

export const closeRepairOrder = (id: number) => async (dispatch: AppDispatch) => {
  dispatch({ type: MemberActionTypes.CLOSE_REPAIR_ORDER });
  try {
    const response = await clientApi.put(`${Endpoints.ros}/${id}/close`, {});
    if (response.status === 200) {
      dispatch({ type: MemberActionTypes.REPAIR_ORDER_CLOSED });
      dispatch({ type: MemberActionTypes.RO_OUTDATED });

      showToast({ message: 'Repair order closed successfully' });
    } else {
      throw new Error(`Failed to close Repair Order.${CONTACT_MESSAGE}`);
    }
  } catch (error) {
    dispatch(handleError(error));
  }
};

export const ROS_STATE_PROPS = {
  ROS: 'ros',
  ADVISOR_DASHBOARD: 'advisorDashboard',
} as const;

export type RosStateProp = (typeof ROS_STATE_PROPS)[keyof typeof ROS_STATE_PROPS];

const ROS_STATE_PROPS_SET: ReadonlySet<string> = new Set(Object.values(ROS_STATE_PROPS));

export const isRosStateProp = (value: string): value is RosStateProp =>
  ROS_STATE_PROPS_SET.has(value);

export const getRos =
  (requestParams?: Record<string, unknown>, stateProp?: RosStateProp, doNotReceive = false) =>
  async (dispatch: AppDispatch) => {
    // `doNotReceive` performs a pure fetch (used by the full-dataset export) without touching
    // the displayed grid's Redux state. Skipping GET_ROS/LOADING_* also avoids leaving
    // `isFetchingROs` stuck true, since only RECEIVE_ROS resets it.
    if (!doNotReceive) {
      dispatch({ type: MemberActionTypes.GET_ROS, stateProp });
      dispatch({ type: MemberActionTypes.LOADING_START });
    }
    const params = buildRosParams(requestParams);
    const endpoint = stateProp ? Endpoints[stateProp] : Endpoints.ros;
    try {
      const response = await clientApi.get(endpoint, { params });
      if (response.status === 200 && !doNotReceive) {
        dispatch({
          type: MemberActionTypes.RECEIVE_ROS,
          data: response.data,
          stateProp,
        });
      }
      return response;
    } catch (error) {
      return dispatch(handleError(error));
    } finally {
      if (!doNotReceive) dispatch({ type: MemberActionTypes.LOADING_END });
    }
  };

export const pollForROs =
  (requestParams?: Record<string, unknown>) => async (dispatch: AppDispatch) => {
    try {
      const {
        openModals: openModalBefore,
        isLoading: isLoadingBefore,
        isFetchingROs: isFetchingROsBefore,
      } = getMemberState();
      if (isLoadingBefore || isFetchingROsBefore || openModalBefore.length > 0) {
        return dispatch({ type: MemberActionTypes.POLL_ROS_CANCEL });
      }
      dispatch({ type: MemberActionTypes.POLL_ROS_GET });
      const params = buildRosParams(requestParams);
      const response = await clientApi.get(Endpoints.ros, { params });

      const { openModals: openModalAfter, isLoading: isLoadingAfter } = getMemberState();
      if (isLoadingAfter || openModalAfter.length > 0) {
        return dispatch({ type: MemberActionTypes.POLL_ROS_CANCEL });
      }
      if (response?.status === 200) {
        dispatch({
          type: MemberActionTypes.POLL_ROS_RECEIVE,
          data: response.data,
        });
      }
    } catch (error: unknown) {
      dispatch({ type: MemberActionTypes.POLL_ROS_CANCEL });
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        return;
      }
      return dispatch(handleError(error));
    }
  };

export const getRepairOrderInteractions =
  (repairOrderId: number) => async (dispatch: AppDispatch) => {
    dispatch({ type: MemberActionTypes.LOADING_START });
    dispatch({ type: 'GET_REPAIR_ORDER_INTERACTIONS' });

    try {
      const response = await clientApi.get(`${Endpoints.repairOrderInteractions}`, {
        params: { repairOrderId },
      });

      if (response.status === 200) {
        dispatch({
          type: MemberActionTypes.RECEIVE_REPAIR_ORDER_INTERACTIONS,
          data: response.data,
        });
        return true;
      } else {
        throw new Error(`Failed to get repair order interactions.${CONTACT_MESSAGE}`);
      }
    } catch (error) {
      dispatch(handleError(error));
    } finally {
      dispatch({ type: MemberActionTypes.LOADING_END });
    }
  };

export const clearSelectedRepairOrder = () => (dispatch: AppDispatch) => {
  dispatch({ type: MemberActionTypes.CLEAR_SELECTED_REPAIR_ORDER });
};
